/* global globalThis */
import React, { useEffect, useState } from "react";
import "./Dashboard.css";
// import { showError } from "../../../common/Utils";
import axiosClientPayload from "../../../../../api-client/AxiosClientPayload";
import { toast } from "react-toastify";
import { RotateCcw } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// PII KPI Card Component
const PIIKPICard = ({ title, value, index, unit = "" }) => {
  const colors = ["#003366", "#fc8716", "#fccb02", "#8dbe46", "#00a651"];

  const getDisplayValue = () => {
    if (title.toLowerCase().includes("score")) {
      return `${value}${unit}`;
    }
    return value;
  };

  const getScoreColor = () => {
    if (title.toLowerCase().includes("score")) {
      if (value < 60) return "#ef4444"; // red
      if (value >= 60 && value <= 80) return "#fc8716"; // orange
      return "#22c55e"; // green
    }
    return colors[index];
  };

  const getSubtext = () => {
    if (title.toLowerCase().includes("status")) {
      return "Current health";
    }
    if (title.toLowerCase().includes("score")) {
      return "Success rate";
    }
    return "";
  };

  return (
    <div className="kpi-card" style={{ flex: '1 1 0%', minWidth: 0 }}>
      <h2 className="kpi-title">{title}</h2>
      <p
        className="text-2xl font-bold mt-2"
        style={{
          color: getScoreColor()
        }}
      >
        {getDisplayValue()}
      </p>
      <span className="kpi-percentage" style={{ fontSize: "12px", color: "#9ca3af" }}>
        {getSubtext()}
      </span>
    </div>
  );
};

// Dimension Summary Table Component
const DimensionSummaryTable = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6" style={{ height: '100%' }}>
        <h2 className="text-2xl font-semibold text-blue-900 mb-4">
          Dimension Summary
        </h2>
        <div className="text-center text-gray-500 py-8">
          No dimension data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6" style={{ maxHeight: '380px', display: 'flex', flexDirection: 'column' }}>
      <h2 className="text-2xl font-semibold text-blue-900 mb-4">
        Dimension Summary
      </h2>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', minHeight: 0 }}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dimension
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Records
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Success Records
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Failed Records
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Success Rate
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((dimension, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {dimension.dimension}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {dimension.total_records.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                  {dimension.success_records.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                  {dimension.failed_records.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${dimension.percentage}%` }}
                      ></div>
                    </div>
                    <span className="font-medium text-gray-900">
                      {dimension.percentage.toFixed(1)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Analysis History Table Component
const AnalysisHistoryTable = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6" style={{ height: '100%' }}>
        <h2 className="text-2xl font-semibold text-blue-900 mb-4">
          Analysis History
        </h2>
        <div className="text-center text-gray-500 py-8">
          No analysis history available
        </div>
      </div>
    );
  }

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6" style={{ maxHeight: '380px', display: 'flex', flexDirection: 'column' }}>
      <h2 className="text-2xl font-semibold text-blue-900 mb-4">
        Recent Scans
      </h2>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', minHeight: 0 }}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Analysis ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tables Analyzed
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Overall Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((analysis, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                  {analysis.analysis_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {analysis.tables_analyzed}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${analysis.overall_score >= 80
                      ? "bg-green-100 text-green-800"
                      : analysis.overall_score >= 60
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                      }`}
                  >
                    {analysis.overall_score.toFixed(1)}%
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {formatDate(analysis.timestamp)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Dimension Bar Chart Component
const DimensionBarChart = ({ data }) => {
  if (!data || !data.success || !data.failed) {
    return null;
  }

  const chartData = data.success.labels.map((label, index) => {
    const success = data.success.values[index];
    const failed = data.failed.values[index];
    const total = success + failed;
    const successPct = total > 0 ? ((success / total) * 100).toFixed(1) : 0;
    const failedPct = total > 0 ? ((failed / total) * 100).toFixed(1) : 0;
    return { label, success, failed, total, successPct, failedPct };
  });

  const maxTotal = Math.max(...chartData.map(d => d.total));

  // Generate X-axis tick values
  const tickCount = 6;
  const tickStep = Math.ceil(maxTotal / tickCount / 500) * 500;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => i * tickStep).filter(t => t <= maxTotal + tickStep);

  const labelWidth = 110;
  const chartAreaWidth = '100%';

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6" style={{ maxHeight: '380px', display: 'flex', flexDirection: 'column' }}>
      <h2 className="text-2xl font-semibold text-blue-900 mb-4">
        Dimension Level Analysis
      </h2>

      {/* Legend */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#EF4444' }}></div>
          <span style={{ fontSize: '12px', color: '#6B7280' }}>Bad Records</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#22c55e' }}></div>
          <span style={{ fontSize: '12px', color: '#6B7280' }}>Good Records</span>
        </div>
      </div>

      {/* Scrollable chart */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div style={{ position: 'relative' }}>
          {/* Bars */}
          {chartData.map((item, index) => {
            const failedWidth = (item.failed / maxTotal) * 100;
            const successWidth = (item.success / maxTotal) * 100;
            return (
              <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', height: '32px' }}>
                {/* Label */}
                <div style={{ width: `${labelWidth}px`, flexShrink: 0, fontSize: '13px', fontWeight: '500', color: '#374151', textAlign: 'right', paddingRight: '12px' }}>
                  {item.label}
                </div>
                {/* Stacked Bar */}
                <div style={{ flex: 1, display: 'flex', height: '28px', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#E5E7EB' }}>
                  {/* Failed (Red) segment */}
                  {item.failed > 0 && (
                    <div style={{
                      width: `${failedWidth}%`,
                      backgroundColor: '#EF4444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: failedWidth > 8 ? '0' : '0',
                      flexShrink: 0,
                    }}>
                      {failedWidth > 8 && (
                        <span style={{ color: '#fff', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {item.failed.toLocaleString()} ({item.failedPct}%)
                        </span>
                      )}
                    </div>
                  )}
                  {/* Success (Green) segment */}
                  {item.success > 0 && (
                    <div style={{
                      width: `${successWidth}%`,
                      backgroundColor: '#22c55e',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {successWidth > 8 && (
                        <span style={{ color: '#fff', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {item.success.toLocaleString()} ({item.successPct}%)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* X-Axis */}
          <div style={{ display: 'flex', marginTop: '8px', height: '24px' }}>
            <div style={{ width: `${labelWidth}px`, flexShrink: 0 }}></div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between' }}>
              {ticks.map((tick, i) => (
                <span key={i} style={{ fontSize: '11px', color: '#6B7280' }}>
                  {tick.toLocaleString()}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// High Risk PII Component 
const HighRiskPII = ({ piiSummary }) => {
  const hasData = piiSummary && piiSummary.high_risk_pii && piiSummary.high_risk_pii.length > 0;

  // Generate scatter chart data with non-overlapping distribution
  const generateScatterData = () => {
    if (!hasData) return [];
    
    const items = piiSummary.high_risk_pii;
    const count = items.length;
    
    // Calculate bubble sizes first to determine spacing
    const bubbles = items.map((item, index) => {
      const minSize = 10;
      const maxSize = 18;
      const size = Math.min(maxSize, Math.max(minSize, minSize + (item.count / 4)));
      return { ...item, size, index };
    });

    // Sort by size (largest first) for better packing
    bubbles.sort((a, b) => b.size - a.size);

    const placed = [];
    const centerX = 50;
    const centerY = 50;
    const minDistance = 3; // Minimum distance between bubble edges

    bubbles.forEach((bubble, idx) => {
      let x, y, attempts = 0;
      let validPosition = false;

      while (!validPosition && attempts < 100) {
        // Spiral placement pattern
        const angle = (idx / count) * 2 * Math.PI + (attempts * 0.5);
        const radius = 15 + (attempts * 2) + (idx * 3);
        
        x = centerX + radius * Math.cos(angle);
        y = centerY + radius * Math.sin(angle);

        // Check if position is valid (no overlap)
        validPosition = placed.every(other => {
          const dx = x - other.x;
          const dy = y - other.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const minRequired = bubble.size + other.size + minDistance;
          return distance >= minRequired;
        });

        attempts++;
      }

      // Keep within bounds
      x = Math.max(bubble.size + 5, Math.min(100 - bubble.size - 5, x));
      y = Math.max(bubble.size + 5, Math.min(100 - bubble.size - 5, y));

      placed.push({
        x,
        y,
        size: bubble.size,
        label: bubble.pii_type,
        count: bubble.count,
        originalIndex: bubble.index
      });
    });

    // Assign colors
    const colors = [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
      '#8B5CF6', '#EC4899', '#14B8A6', '#F97316',
      '#6366F1', '#10B981'
    ];

    return placed.map(item => ({
      ...item,
      color: colors[item.originalIndex % colors.length]
    }));
  };

  const scatterData = generateScatterData();

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6" style={{ maxHeight: '380px', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        {/* <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" fill="#3B82F6" />
          <path d="M12 2C12 2 12 12 12 12L21.2 16.4" stroke="white" strokeWidth="1.5" fill="none" />
          <path d="M12 12L6.5 6.5" stroke="white" strokeWidth="1.5" fill="none" />
          <path d="M12 2 A10 10 0 0 1 21.2 16.4 L12 12 Z" fill="white" fillOpacity="0.3" />
          <path d="M12 12 L6.5 6.5 A10 10 0 0 1 12 2 Z" fill="white" fillOpacity="0.2" />
        </svg> */}
        <h2 className="text-2xl font-semibold text-blue-900">
          High Risk PII Detected
        </h2>
      </div>

      {!hasData ? (
        <div className="text-center text-gray-500 py-8">
          No high risk PII detected
        </div>
      ) : (
        <div style={{ flex: 1, position: 'relative', minHeight: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg 
            width="100%" 
            height="100%" 
            viewBox="0 0 100 100" 
            preserveAspectRatio="xMidYMid meet"
            style={{ maxHeight: '280px' }}
          >
            {/* Background subtle circles */}
            <circle cx="50" cy="50" r="40" fill="none" stroke="#F3F4F6" strokeWidth="0.2" opacity="0.4" />
            <circle cx="50" cy="50" r="25" fill="none" stroke="#F3F4F6" strokeWidth="0.2" opacity="0.4" />
            
            {scatterData.map((point, index) => {
              // Calculate label position (outside bubble)
              const angleToCenter = Math.atan2(point.y - 50, point.x - 50);
              const labelDistance = point.size + 4; // Distance from bubble center
              const labelX = point.x + labelDistance * Math.cos(angleToCenter);
              const labelY = point.y + labelDistance * Math.sin(angleToCenter);
              
              // Determine text anchor based on position
              let textAnchor = 'middle';
              if (labelX < point.x - 1) textAnchor = 'end';
              else if (labelX > point.x + 1) textAnchor = 'start';
              
              return (
                <g key={index}>
                  {/* Connection line from bubble to label */}
                  <line
                    x1={point.x + (point.size * 0.7) * Math.cos(angleToCenter)}
                    y1={point.y + (point.size * 0.7) * Math.sin(angleToCenter)}
                    x2={labelX}
                    y2={labelY}
                    stroke="#D1D5DB"
                    strokeWidth="0.15"
                    opacity="0.5"
                  />
                  
                  {/* Bubble Shadow */}
                  <circle
                    cx={point.x + 0.3}
                    cy={point.y + 0.3}
                    r={point.size}
                    fill="#000000"
                    opacity="0.08"
                  />
                  
                  {/* Bubble Circle */}
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={point.size}
                    fill={point.color}
                    opacity="0.9"
                    stroke="#ffffff"
                    strokeWidth="0.4"
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.setAttribute('opacity', '1');
                      e.target.setAttribute('r', point.size * 1.15);
                    }}
                    onMouseLeave={(e) => {
                      e.target.setAttribute('opacity', '0.9');
                      e.target.setAttribute('r', point.size);
                    }}
                  >
                    <title>{`${point.label}: ${point.count} detections`}</title>
                  </circle>
                  
                  {/* Count inside bubble */}
                  <text
                    x={point.x}
                    y={point.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                      fontSize: '3px',
                      fontWeight: '700',
                      fill: '#ffffff',
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  >
                    {point.count}
                  </text>
                  
                  {/* Label Text Outside - Medium Font */}
                  <text
                    x={labelX}
                    y={labelY}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    style={{
                      fontSize: '3px',
                      fontWeight: '500',
                      fill: '#374151',
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  >
                    {point.label.length > 20 
                      ? point.label.substring(0, 17) + '...' 
                      : point.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
};
const PIIDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedConfigId, setSelectedConfigId] = useState(null);

  // Fetch PII Dashboard Data
  const fetchPIIDashboardData = async (configId) => {
    if (!configId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token") || "";

      const response = await axiosClientPayload.post(
        "https://func-owlsightpii-dev-weu-ercydcfpguekd7a5.westeurope-01.azurewebsites.net/api/v1/dashboard",
        {
          token: token,
          org_config_id: configId,
        }
      );

      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching PII dashboard data:", error);
      //   showError(error);
      toast.error("Failed to fetch PII dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      const configId = localStorage.getItem("organization_config_id");

      if (!configId) {
        toast.warning(
          "No configuration selected. Please select a configuration from the header."
        );
        setIsLoading(false);
        return;
      }

      setSelectedConfigId(configId);
      await fetchPIIDashboardData(configId);
    };

    fetchInitialData();

    // Listen for localStorage events
    globalThis.addEventListener("localStorageUpdated", fetchInitialData);
    globalThis.addEventListener("customStorageChange", (e) => {
      if (e.detail?.key === "organization_config_id") {
        fetchInitialData();
      }
    });

    return () => {
      globalThis.removeEventListener("localStorageUpdated", fetchInitialData);
      globalThis.removeEventListener("customStorageChange", fetchInitialData);
    };
  }, []);

  const handleRefresh = () => {
    if (selectedConfigId) {
      fetchPIIDashboardData(selectedConfigId);
    }
  };

  // Show message when no configuration is selected
  if (!selectedConfigId && !isLoading) {
    return (
      <div className="dashboard-container">
        <div className="flex flex-col justify-center items-center h-96">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md text-center">
            <svg
              className="w-16 h-16 text-yellow-500 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              No Configuration Selected
            </h3>
            <p className="text-gray-600">
              Please select an organization and configuration from the header to
              view PII dashboard data.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {isLoading ? (
        <div className="flex flex-col justify-center items-center h-96">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-900 rounded-full animate-spin"></div>
          </div>
          <p className="mt-3 text-sm text-gray-600">
            Loading PII dashboard data...
          </p>
        </div>
      ) : (
        <>
          <div className="tab-content">
            <div className="flex items-center justify-between mb-4">
              {/* <h2 className="text-3xl font-semibold text-blue-900 mt-2">
                PII Analysis Overview
              </h2> */}
              <div className="flex items-end gap-2">
                {/* <button
                  onClick={handleRefresh}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded flex items-center"
                >
                  <RotateCcw className="w-4 h-4 mr-2 mt-0.5" />
                  <span>Refresh</span>
                </button> */}
              </div>
            </div>

            {dashboardData && (
              <>
                {/* KPI Cards */}
                <div className="dashboard-cards">
                  <div className="flex gap-4 mb-6" style={{ display: 'flex', flexWrap: 'nowrap' }}>
                    <PIIKPICard
                      title="Total Analyses Run"
                      value={dashboardData.total_analyses_run}
                      index={0}
                    />
                    <PIIKPICard
                      title="Total Tables Analyzed"
                      value={dashboardData.total_tables_analyzed}
                      index={1}
                    />
                    <PIIKPICard
                      title="Total Records Analyzed"
                      value={dashboardData.total_records_analyzed.toLocaleString()}
                      index={2}
                    />
                    <PIIKPICard
                      title="Overall Score"
                      value={dashboardData.overall_score.toFixed(2)}
                      unit="%"
                      index={3}
                    />
                    <PIIKPICard
                      title="Total PII Detected"
                      value={dashboardData.pii_types_summary?.total_unique_types || 0}
                      index={4}
                    />
                  </div>

                  {/* Analysis History & High Risk PII Side by Side */}
                  <div style={{ display: 'flex', gap: '24px', alignItems: 'stretch' }}>
                    {/* Left - Analysis History */}
                    <div style={{ flex: '1 1 0%', minWidth: 0 }}>
                      <AnalysisHistoryTable data={dashboardData.analysis_history} />
                    </div>

                    {/* Right - High Risk PII */}
                    <div style={{ flex: '1 1 0%', minWidth: 0 }}>
                      <HighRiskPII piiSummary={dashboardData.pii_types_summary} />
                    </div>
                  </div>

                  {/* Dimension Summary */}
                  <DimensionSummaryTable data={dashboardData.dimension_summary} />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PIIDashboard;